// از دادهٔ خامِ `admin_journeys` تا چیزی که می‌شود کشیدش.
//
// عمداً در core و نه در SQL: آستانه‌ها (چند پله، چند صفحه در هر ستون، مرزِ
// «بازدیدِ فوری») تصمیم‌های نمایشی‌اند و عوض کردنشان نباید مایگریشن بخواهد.
// همان تقسیمِ کاری که `sourceTest.ts` دارد.
//
// و چون این‌جا هیچ وابستگیِ عارضه‌داری نیست، `npm test` با اعدادِ ساختگی
// می‌آزمایدش — بدونِ دیتابیس، بدونِ مرورگر.
import type { SankeyLink, SankeyNode } from './sankey.ts';

/* ─────────────────────────── ورودی ─────────────────────────── */

export interface JourneySession {
  session_id: string;
  started: string;
  ended: string;
  surface: 'web' | 'mobile';
  signed_in: boolean;
  views: number;
  taps: number;
  entry: string;
  ref: string | null;
  ms: number;
  first_screen: string | null;
  exit_screen: string | null;
}

export interface JourneyStep {
  session_id: string;
  step: number;
  screen: string;
  came_from: string | null;
  via: string | null;
  /** `null` یعنی «نمی‌دانیم چقدر ماند» — نه «فوری رفت». */
  ms: number | null;
}

export interface JourneyTap {
  session_id: string;
  step: number;
  screen: string | null;
  label: string;
}

export interface JourneyData {
  sessions: JourneySession[];
  steps: JourneyStep[];
  taps: JourneyTap[];
  window?: { sessions: number; from: string | null; to: string | null };
}

/* ─────────────────────────── خروجی ─────────────────────────── */

export interface ScreenStat {
  screen: string;
  /** چند بار باز شد. */
  views: number;
  /** در چند نشستِ متمایز. */
  sessions: number;
  /** چند بار نقطه‌ی **شروعِ** نشست بود. */
  entries: number;
  /** چند بار آخرین صفحه‌ی نشست بود. */
  exits: number;
  /** `exits / views` — بینِ ۰ و ۱. */
  exitRate: number;
  /** میانه و صدکِ ۹۰ی مدتِ ماندن، به میلی‌ثانیه. `null` یعنی داده‌ای نبود. */
  medianMs: number | null;
  p90Ms: number | null;
  totalMs: number;
}

export interface ButtonStat {
  screen: string;
  label: string;
  taps: number;
  sessions: number;
}

export interface EntryStat { entry: string; ref: string | null; sessions: number }

export interface JourneyFlow { nodes: SankeyNode[]; links: SankeyLink[] }

export interface JourneyReport {
  screens: ScreenStat[];
  buttons: ButtonStat[];
  entries: EntryStat[];
  flow: JourneyFlow;
  totals: {
    sessions: number;
    /** نشستی که فقط یک صفحه دید و رفت. */
    bounced: number;
    bounceRate: number;
    medianStepsPerSession: number;
    medianSessionMs: number | null;
  };
}

/* ─────────────────────────── تنظیمات ─────────────────────────── */

export interface JourneyOptions {
  /** چند ستونِ اولِ سفر در نمودار بیاید. */
  maxSteps?: number;
  /** در هر ستون چند صفحه‌ی پرتکرار جدا بماند؛ بقیه «بقیه» می‌شوند. */
  topPerStep?: number;
  /**
   * فقط نشست‌های یک سطح (WHOSUP-242). نبودنش یعنی هر دو، مثلِ قبل.
   *
   * ⚠ فیلترِ سمتِ کلاینت و نه کوئریِ تازه، چون شدنی است: هر نشستِ
   * `admin_journeys` از قبل `surface` دارد و پله‌ها و لمس‌ها با
   * `session_id` به همان نشست وصل‌اند. یعنی تعویضِ سطح رفت‌وبرگشتِ شبکه
   * نمی‌خواهد.
   *
   * ⚠ پنجره **عوض نمی‌شود**: داده همان «تازه‌ترین N نشست»ِ هر دو سطح
   * است و این‌جا فقط بخشی‌اش نگه داشته می‌شود. پس «۴۰ نشستِ موبایل»
   * یعنی چهل تا از آن N، نه تازه‌ترین چهل نشستِ موبایل — و رابط همین را
   * می‌نویسد، وگرنه دو سطحِ کم‌ترافیک و پرترافیک ناجور مقایسه می‌شوند.
   */
  surface?: JourneySession['surface'] | 'all';
}

const DEFAULTS = { maxSteps: 5, topPerStep: 6, surface: 'all' as const };

/** برچسبِ گرهِ «بقیه‌ی صفحه‌ها» و گرهِ خروج — رابط همین دو را ترجمه‌شده نشان می‌دهد. */
export const OTHER = 'other';
export const EXIT = 'exit';

/* ─────────────────────────── ساخت ─────────────────────────── */

export function buildJourneyReport(data: JourneyData, opts: JourneyOptions = {}): JourneyReport {
  const o = { ...DEFAULTS, ...opts };
  const all = data.sessions ?? [];
  const sessions = o.surface === 'all' ? all : all.filter((s) => s.surface === o.surface);
  // پله و لمس ستونِ سطح ندارند و لازم هم نیست: نشست است که سطح دارد، و هر
  // پله‌ای مالِ یک نشست است. همان مجموعه‌ی شناسه یعنی همان فیلتر.
  const keep = new Set(sessions.map((s) => s.session_id));
  const mine = <T extends { session_id: string }>(rows: T[]): T[] =>
    (o.surface === 'all' ? rows : rows.filter((r) => keep.has(r.session_id)));
  const steps = mine(data.steps ?? []);
  const taps = mine(data.taps ?? []);

  return {
    screens: screenStats(sessions, steps),
    buttons: buttonStats(taps),
    entries: entryStats(sessions),
    flow: buildFlow(steps, o.maxSteps, o.topPerStep),
    totals: totals(sessions, steps),
  };
}

/* ─────────────── صفحه‌ها: چند بار، چقدر، و کجا تمام شد ─────────────── */

function screenStats(sessions: JourneySession[], steps: JourneyStep[]): ScreenStat[] {
  const acc = new Map<string, {
    views: number; sessions: Set<string>; entries: number; exits: number; times: number[];
  }>();
  const get = (screen: string) => {
    let a = acc.get(screen);
    if (!a) { a = { views: 0, sessions: new Set(), entries: 0, exits: 0, times: [] }; acc.set(screen, a); }
    return a;
  };

  for (const s of steps) {
    const a = get(s.screen);
    a.views += 1;
    a.sessions.add(s.session_id);
    // ⚠ فقط مدتِ **معلوم** وارد میانه می‌شود. صفر گذاشتن به‌جای «نمی‌دانیم»
    // میانه را به سمتِ صفر می‌کشد و صفحه‌ای که کاربر رویش مانده و بعد اپ را
    // کشته، «صفحه‌ای که هیچ‌کس رویش نمی‌ماند» به نظر می‌رسد.
    if (s.ms !== null && s.ms >= 0) a.times.push(s.ms);
  }
  for (const s of sessions) {
    if (s.first_screen) get(s.first_screen).entries += 1;
    if (s.exit_screen) get(s.exit_screen).exits += 1;
  }

  return [...acc.entries()]
    .map(([screen, a]) => ({
      screen,
      views: a.views,
      sessions: a.sessions.size,
      entries: a.entries,
      exits: a.exits,
      exitRate: a.views ? a.exits / a.views : 0,
      medianMs: quantile(a.times, 0.5),
      p90Ms: quantile(a.times, 0.9),
      totalMs: a.times.reduce((x, y) => x + y, 0),
    }))
    .sort((x, y) => y.views - x.views);
}

/* ─────────────────────────── دکمه‌ها ─────────────────────────── */

function buttonStats(taps: JourneyTap[]): ButtonStat[] {
  const acc = new Map<string, { screen: string; label: string; taps: number; sessions: Set<string> }>();
  for (const t of taps) {
    const screen = t.screen ?? '?';
    const key = `${screen}:${t.label}`;
    let a = acc.get(key);
    if (!a) { a = { screen, label: t.label, taps: 0, sessions: new Set() }; acc.set(key, a); }
    a.taps += 1;
    a.sessions.add(t.session_id);
  }
  return [...acc.values()]
    .map((a) => ({ screen: a.screen, label: a.label, taps: a.taps, sessions: a.sessions.size }))
    .sort((x, y) => y.taps - x.taps);
}

/* ─────────────────────────── ورودی‌ها ─────────────────────────── */

function entryStats(sessions: JourneySession[]): EntryStat[] {
  const acc = new Map<string, EntryStat>();
  for (const s of sessions) {
    const key = `${s.entry}:${s.ref ?? ''}`;
    const a = acc.get(key);
    if (a) a.sessions += 1;
    else acc.set(key, { entry: s.entry, ref: s.ref ?? null, sessions: 1 });
  }
  return [...acc.values()].sort((x, y) => y.sessions - x.sessions);
}

/* ─────────────────────────── جریان ─────────────────────────── */

/**
 * نمودارِ جریانِ رفتار: ستونِ اول اولین صفحه‌ی هر نشست، ستونِ دوم دومی، و
 * همین‌طور تا `maxSteps`.
 *
 * ═══ چرا ستون‌ها «پله» هستند و نه خودِ صفحه‌ها ═══
 *
 * گرافِ صفحه‌به‌صفحه حلقه دارد (کشف → رویداد → کشف) و سنکی حلقه را نمی‌کشد.
 * پله‌ای کردنش هم مشکل را حل می‌کند هم سوالِ درست‌تری جواب می‌دهد: «کاربر
 * از اولین صفحه به کجا می‌رود» نه «این دو صفحه رویِ هم چند بار کنارِ هم
 * بودند».
 *
 * آن سوالِ دیگر — «روی هم چند نفر وارد این صفحه شدند» — جای خودش را دارد و
 * در `journeyGraph.ts` جواب می‌گیرد، با یک گره به ازای هر آدرس و یال‌های
 * جهت‌دار. دو نمودار، دو تب، هیچ‌کدام جانشینِ آن یکی.
 *
 * ⚠ در هر ستون فقط چند صفحه‌ی پرتکرار جدا می‌مانند و بقیه در یک گرهِ
 * «بقیه» جمع می‌شوند. بدونِ این، ستونِ سوم سی گرهِ یک‌نفره می‌شود و نمودار
 * از خوانده شدن می‌افتد. جمع همیشه درست می‌ماند — چیزی حذف نمی‌شود.
 *
 * ⚠ نشستی که در پله‌ی i تمام می‌شود به گرهِ «خروج» وصل می‌شود و نه به هیچ.
 * ریزش باید دیده شود؛ جریانی که بی‌صدا کم می‌شود همان داشبوردی است که
 * سرِ ۰۱۱۵ نوشته شده «کم بشمارد از خالی بدتر است».
 */
function buildFlow(steps: JourneyStep[], maxSteps: number, topPerStep: number): JourneyFlow {
  const bySession = new Map<string, JourneyStep[]>();
  for (const s of steps) {
    const arr = bySession.get(s.session_id);
    if (arr) arr.push(s); else bySession.set(s.session_id, [s]);
  }
  for (const arr of bySession.values()) arr.sort((a, b) => a.step - b.step);

  // پرتکرارترین صفحه‌های هر پله.
  const perStep: Map<number, Map<string, number>> = new Map();
  for (const arr of bySession.values()) {
    arr.slice(0, maxSteps).forEach((s, i) => {
      const m = perStep.get(i + 1) ?? new Map<string, number>();
      m.set(s.screen, (m.get(s.screen) ?? 0) + 1);
      perStep.set(i + 1, m);
    });
  }
  const keep = new Map<number, Set<string>>();
  for (const [step, m] of perStep) {
    keep.set(step, new Set([...m.entries()]
      .sort((a, b) => b[1] - a[1]).slice(0, topPerStep).map(([s]) => s)));
  }

  const nodeId = (step: number, screen: string): string => {
    const kept = keep.get(step)?.has(screen) ? screen : OTHER;
    return `${step}:${kept}`;
  };
  const exitId = (step: number): string => `${step}:${EXIT}`;

  /** کلیدِ پیوند: `از>به`. شناسه‌ی گره هیچ‌وقت `>` ندارد. */
  const linkCount = new Map<string, number>();
  const nodeSeen = new Map<string, { step: number; screen: string }>();
  const seeNode = (id: string, step: number, screen: string): void => {
    if (!nodeSeen.has(id)) nodeSeen.set(id, { step, screen });
  };

  for (const arr of bySession.values()) {
    const path = arr.slice(0, maxSteps);
    for (let i = 0; i < path.length; i += 1) {
      const step = i + 1;
      const id = nodeId(step, path[i].screen);
      seeNode(id, step, keep.get(step)?.has(path[i].screen) ? path[i].screen : OTHER);
      const isLast = i === path.length - 1;
      // پله‌ی آخرِ **کوتاه‌شده** خروج نیست: نشست ادامه داشته، ما بریدیمش.
      const truncated = isLast && arr.length > maxSteps;
      const nextId = isLast
        ? (truncated ? null : exitId(step))
        : nodeId(step + 1, path[i + 1].screen);
      if (!nextId) continue;
      if (isLast && !truncated) seeNode(nextId, step, EXIT);
      else if (!isLast) {
        seeNode(nextId, step + 1, keep.get(step + 1)?.has(path[i + 1].screen)
          ? path[i + 1].screen : OTHER);
      }
      const key = `${id}>${nextId}`;
      linkCount.set(key, (linkCount.get(key) ?? 0) + 1);
    }
  }

  const nodes: SankeyNode[] = [...nodeSeen.entries()].map(([id, n]) => ({
    id,
    label: n.screen,
    // گرهِ خروج در همان ستونِ پله‌ی خودش می‌ماند، وگرنه ستونِ آخر فقط
    // خروج می‌شد و بقیه‌ی نمودار به هم می‌ریخت.
    layer: n.screen === EXIT ? n.step : n.step - 1,
    terminal: n.screen === EXIT,
  }));
  const links: SankeyLink[] = [...linkCount.entries()].map(([key, value]) => {
    const [from, to] = key.split('>');
    return { from, to, value };
  });
  return { nodes, links };
}

/* ─────────────────────────── جمع‌ها ─────────────────────────── */

function totals(sessions: JourneySession[], steps: JourneyStep[]): JourneyReport['totals'] {
  const perSession = new Map<string, number>();
  for (const s of steps) perSession.set(s.session_id, (perSession.get(s.session_id) ?? 0) + 1);
  const counts = sessions.map((s) => perSession.get(s.session_id) ?? 0);
  const bounced = counts.filter((n) => n <= 1).length;
  return {
    sessions: sessions.length,
    bounced,
    bounceRate: sessions.length ? bounced / sessions.length : 0,
    medianStepsPerSession: quantile(counts, 0.5) ?? 0,
    medianSessionMs: quantile(sessions.map((s) => s.ms).filter((n) => n > 0), 0.5),
  };
}

/* ─────────────────────────── ریاضیِ کوچک ─────────────────────────── */

/**
 * صدکِ خطیِ ساده. `null` وقتی هیچ نمونه‌ای نیست — و این با صفر یکی نیست:
 * «اندازه نگرفتیم» و «صفر بود» دو خبرِ متفاوتند.
 */
export function quantile(values: number[], q: number): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  if (s.length === 1) return s[0];
  const pos = (s.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  if (lo === hi) return s[lo];
  return Math.round(s[lo] + (s[hi] - s[lo]) * (pos - lo));
}
